import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import AdminLayout from '../../layouts/AdminLayout';
import Button from '../../components/common/Button';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  pending_approval: 'bg-yellow-100 text-yellow-700',
  draft: 'bg-gray-100 text-gray-600',
  closed: 'bg-red-100 text-red-700',
  expired: 'bg-red-100 text-red-600',
  paused: 'bg-orange-100 text-orange-700',
};

const APP_STATUS_STYLE = {
  applied:     'bg-blue-50 text-blue-700',
  viewed:      'bg-gray-50 text-gray-600',
  shortlisted: 'bg-yellow-50 text-yellow-700',
  hired:       'bg-green-50 text-green-700',
  rejected:    'bg-red-50 text-red-700',
  withdrawn:   'bg-gray-50 text-gray-500',
};

const EMPTY_FORM = {
  employer_id: '', work_category_id: '', title: '', description: '',
  number_of_workers: 1, location_text: '', wage_min: '', wage_max: '',
  wage_type: 'daily', shift_hours: '', start_date: '', end_date: '',
  application_deadline: '', accommodation: false, food_provided: false,
  travel_provided: false, gender_preference: 'any', urgency: 'medium',
  contact_person: '', contact_mobile: '',
};

export default function AdminRequirements() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [requirements, setRequirements] = useState([]);
  const [pagination, setPagination]     = useState({});
  const [loading, setLoading]           = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);

  const [createModal, setCreateModal]   = useState(false);
  const [createForm, setCreateForm]     = useState(EMPTY_FORM);
  const [employers, setEmployers]       = useState([]);
  const [categories, setCategories]     = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [allSkills, setAllSkills]       = useState([]);
  const [creating, setCreating]         = useState(false);

  const [applicantsReq, setApplicantsReq] = useState(null);
  const [applicants, setApplicants]       = useState([]);
  const [appsLoading, setAppsLoading]     = useState(false);
  const [updatingApp, setUpdatingApp]     = useState(null);

  const statusFilter = searchParams.get('status') || '';

  async function loadRequirements(page = 1) {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/admin/requirements?${params}`);
      setRequirements(res.data.data);
      setPagination(res.data.pagination);
    } finally { setLoading(false); }
  }

  useEffect(() => { loadRequirements(); }, [statusFilter]);

  useEffect(() => {
    Promise.all([
      api.get('/admin/employers?limit=100'),
      api.get('/lookup/work-categories'),
      api.get('/lookup/skills'),
    ]).then(([e, c, s]) => {
      setEmployers(e.data.data || []);
      setCategories(c.data.data || []);
      setAllSkills(s.data.data || []);
    }).catch(() => {});
  }, []);

  async function handleApprove(id, action, reason = '') {
    setActionLoading(true);
    try {
      await api.post(`/admin/requirements/${id}/approve`, { action, reason });
      toast.success(action === 'approve' ? 'Requirement approved' : 'Requirement rejected');
      setRejectTarget(null);
      loadRequirements();
    } finally { setActionLoading(false); }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/admin/requirements', {
        ...createForm,
        number_of_workers: parseInt(createForm.number_of_workers) || 1,
        accommodation:  !!createForm.accommodation,
        food_provided:  !!createForm.food_provided,
        travel_provided: !!createForm.travel_provided,
        skills: selectedSkills.map((id) => ({ skill_id: id, is_mandatory: true })),
      });
      toast.success('Requirement created and published');
      setCreateModal(false);
      setCreateForm(EMPTY_FORM);
      setSelectedSkills([]);
      loadRequirements();
    } finally { setCreating(false); }
  }

  async function openApplicants(req) {
    setApplicantsReq(req);
    setAppsLoading(true);
    try {
      const res = await api.get(`/admin/requirements/${req.id}/applications?limit=50`);
      setApplicants(res.data.data || []);
    } finally { setAppsLoading(false); }
  }

  async function updateAppStatus(appId, status) {
    setUpdatingApp(appId);
    try {
      await api.patch(`/admin/applications/${appId}/status`, { status });
      setApplicants((prev) => prev.map((a) => a.id === appId ? { ...a, status } : a));
      toast.success('Status updated');
    } finally { setUpdatingApp(null); }
  }

  const F = (label, key, type = 'text', extra = {}) => (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-gray-600">{label}</label>
      <input type={type} value={createForm[key] ?? ''}
        onChange={(e) => setCreateForm((p) => ({ ...p, [key]: e.target.value }))}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none"
        {...extra} />
    </div>
  );

  const Sel = (label, key, options) => (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-gray-600">{label}</label>
      <select value={createForm[key] ?? ''} onChange={(e) => setCreateForm((p) => ({ ...p, [key]: e.target.value }))}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-gray-900">Requirements</h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-sm text-gray-500 hidden sm:inline">{pagination.total} total</span>
            <Button onClick={() => setCreateModal(true)}>+ Create</Button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <select value={statusFilter}
            onChange={(e) => { const p = new URLSearchParams(searchParams); p.set('status', e.target.value); setSearchParams(p); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none">
            <option value="">All Status</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="paused">Paused</option>
            <option value="closed">Closed</option>
            <option value="expired">Expired</option>
          </select>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Title', 'Company', 'Location', 'Workers', 'Wage', 'Dates', 'Status', 'Apps', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={9} className="py-12 text-center text-gray-400">Loading...</td></tr>
                ) : requirements.length === 0 ? (
                  <tr><td colSpan={9} className="py-12 text-center text-gray-400">No requirements found</td></tr>
                ) : requirements.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900 max-w-[180px] truncate">{r.title}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-[120px] truncate">{r.company_name}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{r.district || r.location_text || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 text-center">{r.number_of_workers}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{r.wage_min ? `₹${r.wage_min}–${r.wage_max}` : '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {r.start_date ? new Date(r.start_date).toLocaleDateString('en-IN') : '—'}
                      {r.end_date ? ` → ${new Date(r.end_date).toLocaleDateString('en-IN')}` : ''}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-600'}`}>
                        {r.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">{r.applications_count || 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        <button onClick={() => openApplicants(r)}
                          className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200">
                          Applicants
                        </button>
                        {r.status === 'pending_approval' && (
                          <>
                            <button onClick={() => handleApprove(r.id, 'approve')}
                              className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700">✓</button>
                            <button onClick={() => setRejectTarget(r)}
                              className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200">✗</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination.totalPages > 1 && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-gray-500">Page {pagination.page} of {pagination.totalPages}</span>
              <div className="flex gap-2">
                {pagination.hasPrev && <Button size="sm" variant="secondary" onClick={() => loadRequirements(pagination.page - 1)}>← Prev</Button>}
                {pagination.hasNext && <Button size="sm" variant="secondary" onClick={() => loadRequirements(pagination.page + 1)}>Next →</Button>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Create Requirement Modal ── */}
      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-6">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="font-bold text-gray-900 text-lg">Create Requirement</h3>
              <button onClick={() => setCreateModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-5 overflow-y-auto max-h-[80vh]">

              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-600">Employer *</label>
                <select required value={createForm.employer_id}
                  onChange={(e) => setCreateForm((p) => ({ ...p, employer_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none">
                  <option value="">Select employer</option>
                  {employers.map((e) => <option key={e.id} value={e.id}>{e.company_name} — {e.mobile}</option>)}
                </select>
              </div>

              {F('Requirement Title *', 'title', 'text', { required: true, placeholder: 'e.g., Skilled Masons needed' })}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">Work Category</label>
                  <select value={createForm.work_category_id}
                    onChange={(e) => setCreateForm((p) => ({ ...p, work_category_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none">
                    <option value="">Select category</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                {F('Workers Needed *', 'number_of_workers', 'number', { required: true, min: 1 })}
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-600">Description</label>
                <textarea value={createForm.description}
                  onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-20 focus:ring-2 focus:ring-brand-500 focus:outline-none resize-none"
                  placeholder="Describe the work, requirements, location details..." />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {F('Work Location', 'location_text', 'text', { placeholder: 'Village / City / District' })}
                {F('Shift (hrs/day)', 'shift_hours', 'number', { min: 1, max: 24 })}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {F('Start Date', 'start_date', 'date')}
                {F('End Date', 'end_date', 'date')}
                {F('Apply By', 'application_deadline', 'date')}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {F('Wage Min (₹)', 'wage_min', 'number')}
                {F('Wage Max (₹)', 'wage_max', 'number')}
                {Sel('Wage Type', 'wage_type', [['daily','Daily'],['weekly','Weekly'],['monthly','Monthly'],['fixed','Fixed']])}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Sel('Urgency', 'urgency', [['low','Low'],['medium','Medium'],['high','High'],['critical','Critical']])}
                {Sel('Gender Preference', 'gender_preference', [['any','Any'],['male','Male'],['female','Female']])}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {F('Contact Person', 'contact_person')}
                {F('Contact Mobile', 'contact_mobile', 'tel')}
              </div>

              <div className="flex flex-wrap gap-4">
                {[['accommodation','🏠 Accommodation'],['food_provided','🍽️ Food'],['travel_provided','🚌 Travel']].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input type="checkbox" checked={!!createForm[key]}
                      onChange={(e) => setCreateForm((p) => ({ ...p, [key]: e.target.checked }))}
                      className="rounded" />
                    {label}
                  </label>
                ))}
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-600">Required Skills</label>
                <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-lg max-h-36 overflow-y-auto">
                  {allSkills.map((s) => (
                    <button key={s.id} type="button"
                      onClick={() => setSelectedSkills((p) => p.includes(s.id) ? p.filter(x => x !== s.id) : [...p, s.id])}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                        selectedSkills.includes(s.id) ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}>
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2 border-t border-gray-100">
                <Button type="button" variant="secondary" onClick={() => setCreateModal(false)}>Cancel</Button>
                <Button type="submit" fullWidth loading={creating}>Create & Publish</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Applicants Modal ── */}
      {applicantsReq && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-3xl my-6">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="font-bold text-gray-900">Applicants</h3>
                <p className="text-xs text-gray-400 mt-0.5">{applicantsReq.title}</p>
              </div>
              <button onClick={() => setApplicantsReq(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-5 overflow-y-auto max-h-[75vh]">
              {appsLoading ? (
                <div className="text-center py-8 text-gray-400">Loading...</div>
              ) : applicants.length === 0 ? (
                <div className="text-center py-8 text-gray-400">No applicants yet</div>
              ) : (
                <div className="space-y-3">
                  {applicants.map((app) => (
                    <div key={app.id} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-gray-900 text-sm">{app.full_name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${APP_STATUS_STYLE[app.status] || 'bg-gray-100 text-gray-600'}`}>
                              {app.status}
                            </span>
                          </div>
                          <div className="flex gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                            <span>📱 {app.mobile}</span>
                            {app.district && <span>📍 {app.district}</span>}
                            <span>💼 {app.experience_years}y exp</span>
                            {app.daily_wage_min && <span>₹{app.daily_wage_min}{app.daily_wage_max ? `–${app.daily_wage_max}` : ''}/day</span>}
                            <span>Applied {new Date(app.applied_at).toLocaleDateString('en-IN')}</span>
                          </div>
                          {app.note && <p className="text-xs text-gray-600 mt-1.5 bg-gray-50 px-2 py-1 rounded">{app.note}</p>}
                          {app.employer_note && <p className="text-xs text-blue-700 mt-1 bg-blue-50 px-2 py-1 rounded">Note: {app.employer_note}</p>}
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0 flex-wrap">
                          {app.status !== 'hired' && (
                            <button onClick={() => updateAppStatus(app.id, 'hired')} disabled={updatingApp === app.id}
                              className="text-xs bg-green-600 text-white px-2.5 py-1 rounded hover:bg-green-700">Hire</button>
                          )}
                          {app.status !== 'shortlisted' && app.status !== 'hired' && (
                            <button onClick={() => updateAppStatus(app.id, 'shortlisted')} disabled={updatingApp === app.id}
                              className="text-xs bg-yellow-100 text-yellow-700 px-2.5 py-1 rounded hover:bg-yellow-200">Shortlist</button>
                          )}
                          {app.status !== 'rejected' && app.status !== 'hired' && (
                            <button onClick={() => updateAppStatus(app.id, 'rejected')} disabled={updatingApp === app.id}
                              className="text-xs bg-red-100 text-red-700 px-2.5 py-1 rounded hover:bg-red-200">Reject</button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Requirement Modal ── */}
      {rejectTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="font-bold text-gray-900 mb-4">Reject — {rejectTarget.title}</h3>
            <textarea id="reject-reason-req"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-24 focus:ring-2 focus:ring-brand-500 focus:outline-none"
              placeholder="Reason (will be sent to employer)..." />
            <div className="flex gap-3 mt-4">
              <Button variant="secondary" onClick={() => setRejectTarget(null)}>Cancel</Button>
              <Button variant="danger" loading={actionLoading}
                onClick={() => handleApprove(rejectTarget.id, 'reject', document.getElementById('reject-reason-req').value)}>
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
